import React, {useState, useCallback, useEffect} from 'react';
import {useParams, useNavigate, useSearchParams} from 'react-router-dom';
import FormFiller from './FormFiller';
import {Helmet} from 'react-helmet';
import wx from 'weixin-js-sdk'; // 引入微信JS-SDK
import {
    isDomainRegistered,
    getDomainTypeDescription,
    getWechatShareLink
} from './utils/domainUtils';
import {
    saveAuthRedirectPath,
    clearAuthRedirectPath,
    ensureFullPath,
    needsWechatAuth
} from './utils/wechatJumpManager';

import {isWechatBrowser} from './config/wechat';

// 直接使用环境变量
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const IMAGES_BASE_URL = process.env.REACT_APP_images_API_BASE_URL || '';

// 将分享图片URL规范化为完整公网URL（微信要求 imgUrl 必须是绝对路径）
const normalizeShareImgUrl = (img) => {
    if (!img) return '';
    // 已经是完整URL或base64，直接返回
    if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
    // 相对路径：拼接完整域名
    const base = (IMAGES_BASE_URL || window.location.origin).replace(/\/+$/, '');
    return base + (img.startsWith('/') ? img : '/' + img);
};


export default function FormFillerPage() {
    const {formId} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [meta, setMeta] = useState({});
    
    // 获取URL参数中的卡号
    const cardNumber = searchParams.get('cardNumber');

    // 签名参数
    const [signatureData, setSignatureData] = useState(null);

    // 新增：域名相关状态 - 立即初始化
    const [hostname, setHostname] = useState(window.location.hostname);
    const [domainRegistered, setDomainRegistered] = useState(isDomainRegistered(window.location.hostname));

    // 检查微信授权和跳转逻辑
    useEffect(() => {
        const checkWechatAuth = () => {
            console.log('🔍 FormFillerPage: 检查微信授权和跳转逻辑');
            console.log('📍 当前页面:', window.location.pathname);
            console.log('📱 是否微信环境:', isWechatBrowser());

            // 检查是否在微信浏览器中
            if (!isWechatBrowser()) {
                console.log('🔍 非微信环境，跳过授权检查');
                return;
            }
            // 检查是否已经有微信用户信息（已授权成功）
            const wechatUserStr = localStorage.getItem('wechatUser');
            if (wechatUserStr) {
                try {
                    const wechatUser = JSON.parse(wechatUserStr);
                    if (wechatUser && wechatUser.openid) {
                        console.log('✅ FormFillerPage: 检测到已授权用户，清除跳转路径，跳过所有授权检查和跳转');
                        clearAuthRedirectPath();
                        return;
                    }
                } catch (error) {
                    console.log('⚠️ 解析wechatUser失败，继续检查授权逻辑');
                }
            }
            // 保存当前页面路径，用于微信授权后跳转
            const currentPath = window.location.pathname + window.location.search;
            if (needsWechatAuth(currentPath)) {
                const fullPath = ensureFullPath(currentPath);
                saveAuthRedirectPath(fullPath);
                console.log('📝 FormFillerPage: 保存授权后跳转路径:', fullPath);
            } else {
                console.log('🔍 FormFillerPage: 当前页面不需要微信授权，不保存路径:', currentPath);
            }
        };

        // 立即执行检查
        checkWechatAuth();
    }, [formId, domainRegistered, hostname]);

    // 通过ref回传formData
    const handleFormDataLoaded = useCallback((formData) => {
        const wechatShare = formData?.wechatShare || {};
        const newMeta = {
            title: wechatShare.title || formData.formTitle || '表单填写11111',
            desc: wechatShare.desc || formData.formDesc || '欢迎填写表单111111',
            img: normalizeShareImgUrl(wechatShare.img) || normalizeShareImgUrl('/uploads/66bb5010931243e8841309a009d58387.png'),
        };
        setMeta(prev => {
            if (
                prev.title === newMeta.title &&
                prev.desc === newMeta.desc &&
                prev.img === newMeta.img
            ) {
                return prev;
            }
            return newMeta;
        });
    }, []);

    // 新增：获取微信签名参数
    useEffect(() => {
        if (!isWechatBrowser() || !meta.title) return;
        const fetchSignature = async () => {
            const url = encodeURIComponent(window.location.href.split('#')[0]);
            const res = await fetch(`${API_BASE_URL}/wechat-signature?url=${url}`);
            const data = await res.json();
            setSignatureData(data);
        };
        fetchSignature();
    }, [meta]);

    // 新增：每次打开表单填写页面，微信授权后自动保存预览记录
    useEffect(() => {
        // 获取微信用户信息
        const wechatUser = JSON.parse(localStorage.getItem('wechatUser') || '{}');
        const openId = wechatUser.openid || '';
        const nickName = wechatUser.nickname || '';
        if (formId && openId && nickName) {
            fetch(`${API_BASE_URL}/forms/savePreviewForm`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    formId,
                    openId,
                    nickName
                })
            });
        }
    }, [formId]);

    // 新增：初始化微信JS-SDK并设置分享参数
    useEffect(() => {
        if (!isWechatBrowser() || !signatureData || !meta.title) return;

        // 检查域名是否已备案
        const currentHostname = window.location.hostname;
        const currentDomainRegistered = isDomainRegistered(currentHostname);

        // 更新状态（如果需要的话）
        if (hostname !== currentHostname) {
            setHostname(currentHostname);
        }
        if (domainRegistered !== currentDomainRegistered) {
            setDomainRegistered(currentDomainRegistered);
        }

        wx.config({
            debug: false,
            appId: signatureData.appId,
            timestamp: signatureData.timestamp,
            nonceStr: signatureData.nonceStr,
            signature: signatureData.signature,
            jsApiList: [
                'updateAppMessageShareData',
                'updateTimelineShareData',
                'onMenuShareAppMessage',  // 兼容旧版本
                'onMenuShareTimeline'     // 兼容旧版本
            ]
        });

        wx.ready(function () {
            console.log('wx.ready');

            // 使用域名工具获取分享链接
            const shareLink = getWechatShareLink(currentHostname, formId);

            console.log('设置微信分享link:', shareLink);
            console.log('域名备案状态:', currentDomainRegistered ? '已备案' : '未备案');
            console.log('域名类型:', getDomainTypeDescription(currentHostname));

            // 新版本分享接口
            wx.updateAppMessageShareData({
                title: meta.title,
                desc: meta.desc,
                link: shareLink,
                imgUrl: meta.img,
                success: function () {
                    console.log('分享参数设置成功', shareLink);
                },
                fail: function (res) {
                    console.log('分享参数设置失败:', res);
                }
            });

            wx.updateTimelineShareData({
                title: meta.title,
                link: shareLink,
                imgUrl: meta.img,
                success: function () {
                    console.log('朋友圈参数设置成功', shareLink);
                },
                fail: function (res) {
                    console.log('朋友圈参数设置失败:', res);
                }
            });

            // 兼容旧版本分享接口（作为备用）
            wx.onMenuShareAppMessage({
                title: meta.title,
                desc: meta.desc,
                link: shareLink,
                imgUrl: meta.img,
                success: function () {
                    console.log('旧版本分享参数设置成功');
                }
            });

            wx.onMenuShareTimeline({
                title: meta.title,
                link: shareLink,
                imgUrl: meta.img,
                success: function () {
                    console.log('旧版本朋友圈参数设置成功');
                }
            });
        });

        wx.error(function (res) {
            console.error('微信JS-SDK配置失败', res);
        });
    }, [signatureData, meta, formId]);

    // useEffect(() => {
    //   localStorage.setItem('fromWechatForm', '1');
    // }, []);

    return (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta property="og:title" content={meta.title}/>
                <meta property="og:description" content={meta.desc}/>
                <meta property="og:image" content={meta.img}/>
                <meta property="og:type" content="website"/>
                <meta property="og:url" content={window.location.href}/>
                <meta name="wechat-title" content={meta.title}/>
                <meta name="wechat-desc" content={meta.desc}/>
                <meta name="wechat-img" content={meta.img}/>
            </Helmet>
            <div>
                {/* 域名备案状态提示 */}
                {/* {isWechatBrowser() && (
          <div style={{
            background: domainRegistered ? '#f6ffed' : '#fff7e6',
            border: domainRegistered ? '1px solid #b7eb8f' : '1px solid #ffd591',
            borderRadius: '8px',
            padding: '12px 16px',
            margin: '16px',
            fontSize: '14px',
            color: domainRegistered ? '#389e0d' : '#d46b08'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>{domainRegistered ? '✅' : '⚠️'} 域名备案状态：</strong>
              <span>{domainRegistered ? '已备案' : '未备案'}</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>当前域名：</strong>
              <span>{hostname}</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>域名类型：</strong>
              <span>{getDomainTypeDescription(hostname)}</span>
            </div>
            <div>
              <strong>分享链接：</strong>
              <span style={{ wordBreak: 'break-all', fontSize: '12px' }}>
                {getWechatShareLink(hostname, formId)}
              </span>
            </div>
          </div>
        )} */}
                <FormFiller 
                    formId={formId} 
                    onFormDataLoaded={handleFormDataLoaded}
                    cardNumber={cardNumber}
                />
            </div>
        </>
    );
} 